import baseApi from '@/modules/services/base';
import {
  ElaCategoryType,
  ElearningCategoriesDataType,
  ElearningCategoriesPerTenant,
  ElearningCategoryUpdatePayloadType,
  ElearningCategoryUpdateResponseType,
} from '@/modules/types/elearningCategories';
import {
  ElearningCategoriesListTag,
  ElearningCategoriesAllowedListTag,
  TenantProfileTag,
  ElearningCategoryTag,
} from './tags';
import { PaginationParams, PaginationType } from '../types/elearning';

const elearningCategoriesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getElearningCategories: build.query<
      ElearningCategoriesDataType,
      void | PaginationParams
    >({
      query: (params) => ({
        url: 'elearning-categories/list',
        params: params ?? undefined,
      }),
      providesTags: [ElearningCategoriesListTag],
    }),
    getElearningCategoriesV2: build.query<
      PaginationType<ElaCategoryType>,
      void | PaginationParams
    >({
      query: (params) => ({
        url: 'elearning-categories/category-list',
        params: params ?? undefined,
      }),
      providesTags: [ElearningCategoriesListTag],
    }),
    getElearningCategory: build.query<
      ElaCategoryType & PaginationType<{ id: string; name: string }>,
      [string, PaginationParams]
    >({
      query: ([id, params]) => ({
        url: `elearning-categories/${id}`,
        params: params ?? undefined,
      }),
      providesTags: [ElearningCategoryTag],
    }),
    getElearningCategoriesPerTenant: build.query<
      ElearningCategoriesPerTenant,
      void
    >({
      query: () => ({
        url: 'elearning-categories/list-tenant-categories',
      }),
      providesTags: [ElearningCategoriesListTag],
    }),
    getElearningAllowedCategories: build.query<
      ElearningCategoriesDataType,
      void | PaginationParams
    >({
      query: (params) => ({
        url: 'elearning-categories/list-allowed-categories',
        params: params ?? undefined,
      }),
      providesTags: [ElearningCategoriesAllowedListTag],
    }),
    updateCategoryAccess: build.mutation<
      ElearningCategoryUpdateResponseType,
      ElearningCategoryUpdatePayloadType
    >({
      query: (data: ElearningCategoryUpdatePayloadType) => ({
        url: `tenant-profiles/update-category-access`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TenantProfileTag],
    }),
    createElearningCategories: build.mutation<
      ElaCategoryType,
      { name: string; isEnabled: boolean; isActiveByDefault?: boolean }
    >({
      query: (data: {
        name: string;
        isEnabled: boolean;
        isActiveByDefault?: boolean;
      }) => ({
        url: 'elearning-categories',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        TenantProfileTag,
        ElearningCategoriesAllowedListTag,
        ElearningCategoriesListTag,
        ElearningCategoryTag,
      ],
    }),
    updateElearningCategories: build.mutation<
      ElaCategoryType,
      {
        name: string;
        isEnabled: boolean;
        isActiveByDefault?: boolean;
        id: string;
      }
    >({
      query: (data: {
        name: string;
        isEnabled: boolean;
        isActiveByDefault?: boolean;
        id: string;
      }) => ({
        url: `elearning-categories/${data.id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [
        TenantProfileTag,
        ElearningCategoriesAllowedListTag,
        ElearningCategoriesListTag,
        ElearningCategoryTag,
      ],
    }),
  }),
  overrideExisting: false,
});

export default elearningCategoriesApi;
